import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Ban, ArrowLeft, RefreshCcw, Trash2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlacklistAdmin = ({ onBack }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [filter, setFilter] = useState('');

  const fetchBlacklist = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/blacklist`);
      setItems(data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlacklist();
  }, []);

  const handleRemove = async (website) => {
    try {
      setRemovingId(website);
      await axios.delete(`${API}/blacklist/${encodeURIComponent(website)}`);
      toast.success(`${website} entsperrt`);
      setItems(prev => prev.filter(i => i.website !== website));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler');
    } finally {
      setRemovingId(null);
    }
  };

  const handleClearAll = async () => {
    if (!window.confirm('Alle Einträge aus der Blacklist entfernen? Gelöschte Firmen können bei der nächsten Suche zurückkommen.')) return;
    try {
      setLoading(true);
      const { data } = await axios.delete(`${API}/blacklist`);
      toast.success(`${data.deleted_count} Einträge entfernt`);
      setItems([]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler');
    } finally {
      setLoading(false);
    }
  };

  const filtered = items.filter(i =>
    !filter ||
    (i.website || '').toLowerCase().includes(filter.toLowerCase()) ||
    (i.title || '').toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <div data-testid="blacklist-admin-page">
      <Card className="mb-6 border-slate-300 bg-gradient-to-r from-slate-50 to-gray-50">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <Button variant="outline" size="sm" onClick={onBack} data-testid="blacklist-back-btn">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Zurück
              </Button>
              <CardTitle className="flex items-center gap-2">
                <Ban className="h-6 w-6 text-slate-700" />
                Blacklist ({items.length})
              </CardTitle>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={fetchBlacklist} data-testid="blacklist-refresh-btn">
                <RefreshCcw className="h-4 w-4 mr-2" />
                Neu laden
              </Button>
              {items.length > 0 && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleClearAll}
                  data-testid="blacklist-clear-all-btn"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Alle entfernen
                </Button>
              )}
            </div>
          </div>
          <p className="text-xs text-slate-600 mt-2 flex items-start gap-1">
            <AlertTriangle className="h-3.5 w-3.5 text-amber-600 mt-0.5 flex-shrink-0" />
            Gelöschte Firmen werden hier gespeichert, damit sie bei neuen Suchen nicht erneut erscheinen. Zum erneuten Erfassen: Eintrag entfernen.
          </p>
        </CardHeader>
        <CardContent>
          <input
            type="text"
            placeholder="Suche nach Website oder Name..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="w-full mb-4 p-2 border border-slate-300 rounded-md text-sm"
            data-testid="blacklist-filter-input"
          />

          {loading ? (
            <div className="text-center py-8 text-slate-500">Wird geladen...</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-8 text-slate-500" data-testid="blacklist-empty-state">
              {items.length === 0 ? 'Keine blockierten Websites' : 'Keine Treffer für den Filter'}
            </div>
          ) : (
            <div className="space-y-2">
              {filtered.map((item) => (
                <div
                  key={item.website}
                  className="flex items-center justify-between p-3 border border-slate-200 rounded-md hover:bg-slate-50 transition"
                  data-testid={`blacklist-item-${item.website}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-900 truncate">
                      {item.title || item.website}
                    </div>
                    <div className="text-xs text-slate-500 truncate">
                      {item.website}
                    </div>
                    <div className="text-xs text-slate-400 mt-1">
                      Grund: {item.reason || '—'} · Gesperrt: {item.blocked_at ? new Date(item.blocked_at).toLocaleDateString('de-DE') : '—'}
                      {item.blocked_by ? ` · von ${item.blocked_by}` : ''}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={removingId === item.website}
                    onClick={() => handleRemove(item.website)}
                    data-testid={`blacklist-remove-${item.website}`}
                  >
                    {removingId === item.website ? '...' : 'Entsperren'}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BlacklistAdmin;
